import THREE from "three";
import { LmvBox3 as Box3 } from "./LmvBox3";
import { LmvMatrix4 as Matrix4 } from "./LmvMatrix4";
import { InstanceCollectionBase } from "./InstanceCollectionBase";
import { MeshFlags } from "./MeshFlags";
import { VertexBufferReader } from './VertexBufferReader';
import { VertexBufferWriter } from './VertexBufferWriter';
import { warnOnce } from "../../application/LoggingUtils";

/** @import { FragmentsObject } from "./InstanceCollectionBase" */

const _tmpMatrix = new Matrix4(true);
const _tmpBox = new Box3();

// Used in applyThemingColorAndVisibility
const tmp1 = new THREE.Color();
const tmp2 = new THREE.Color();

/////////////////////////////////////////////////////////////////////
// helper functions for FragmentList

function isObjectEmpty(obj) {
  for (let prop in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, prop)) {
      return false;
    }
  }
  return true;
}

/////////////////////////////////////////////////////////////////////

/**
 * Represents the full list of all geometry instances associated with
 * a particular 2D model. The order in the list is 1:1 with the fragment list
 * in the source LMV/SVF package file.
 *
 * Note: each entry in this collection literally wraps a THREE.Mesh
 * (see `vizmeshes` and `setMesh()`). "Instance" here refers to the
 * per-fragment slot, mirroring the naming of {@link InstanceCollection3D}.
 * @param {FragmentsObject} fragments - Fragment data parsed from an SVF file.
 * @param {number} modelId - The ID of the model.
 */
export class InstanceCollection2D extends InstanceCollectionBase {
  is2d;
  useThreeMesh;
  vizMeshes;

  // theming (coloring based on id)
  originalColors = []; // if vizmesh[i] has modified vertex-colors  due to theming,  originalColors[i]  stores a copy of the original colors.
  themingOrGhostingNeedsUpdate = {}; // indicates if vertex-colors of vizmesh[i] needs to be updated based on recent theming or ghosting changes (index by fragId)
  themingOrGhostingNeedsUpdateByDbId = {}; // Same as above, but indexed by dbId (for the case where the fragId doesn't exist and might be available later)
  dbIdOpacity = []; // ids with overridden opacity, or hidden by setting alpha to 0

  // ghosting for 2d objects: A ghosted object is reduced in transparency and blended with the pageColor.
  dbIdIsGhosted = [];

  constructor(fragments, modelId) {
    super(fragments, modelId);
    this.is2d = true;

    // 2D F2D files start out with nothing and get filled up as we load.
    this.isFixedSize = false;
    this.useThreeMesh = true; //This code path will be used for 2D drawings, which stream in with unknown number of meshes

    // initial length for arrays of meshes/geometries/flags
    // Can be zero.
    const initialSize = this.fragments.length;

    // This will be the list of all mesh instances in the model.
    // Corresponds to all fragments in the case of SVF.
    this.vizmeshes = new Array(initialSize);

    this.vizflags = new Uint16Array(initialSize); // visibility/highlight mode flags

    if (this.fragments?.visibilityFlags) {
      this.vizflags.set(this.fragments.visibilityFlags);
    } else {
      this.vizflags.fill(MeshFlags.MESH_VISIBLE);
    }
  }

  /** @returns {bool} Always false, 2D fragments do not support hiding lines. */
  get linesHidden() {
    warnOnce("[InstanceCollection2D.linesHidden] is deprecated and will be removed in a future release, hiding lines is not supported for 2D.");
    return false;
  }

  /** @returns {bool} Always false, 2D fragments do not support hiding points. */
  get pointsHidden() {
    warnOnce("[InstanceCollection2D.pointsHidden] is deprecated and will be removed in a future release, hiding points is not supported for 2D.");
    return false;
  }

  /**
   * Set mesh for a fragment, replacing any temporary previous one.
   * @param {number} fragId - Fragment ID
   * @param {Object} meshinfo - Object as defined in Viewer3DImpl.setupMesh(..). Contains:
   *      geometry: instanceof BufferGeometry
   *      material: instance of THREE.Material
   *      matrix:   instanceof THREE.Matrix4
   * @param {bool} updateFragmentData - If true, this.bbox and this.transforms is also updated for this fragment.
   *      Only allowed if this.isFixedSize==true. (otherwise, this.boxes and this.transforms is null)
   * @param {bool} [retainMesh] - If true, meshInfo is a THREE.Mesh and this.useThreeMesh, then
   *      meshInfo will be used as the THREE.Mesh for the fragment.
   */
  setMesh(fragId, meshInfo, updateFragmentData, retainMesh) {
    // Remove any temporary geometry we used for the fragment while it was loading
    const oldGeom = this.vizmeshes[fragId];
    if (oldGeom && oldGeom.parent) {
      oldGeom.parent.remove(oldGeom);
    }

    this.resize(fragId);

    // Remember the mesh in the frag->viz mesh array
    let mesh = undefined;
    if (retainMesh && meshInfo instanceof THREE.Mesh) {
      mesh = meshInfo;

      if (meshInfo.matrix) {
        mesh.matrixWorld.copy(meshInfo.matrix);
      }

      mesh.dbId = mesh.dbId || 0;
    } else {
      mesh = new THREE.Mesh(meshInfo.geometry, meshInfo.material);

      // Copy matrix to mesh.matrix and mesh.matrixWorld
      // Why copying twice?
      if (meshInfo.matrix) {
        if (mesh.matrix) {
          mesh.matrix.copy(meshInfo.matrix);
        }
        mesh.matrixWorld.copy(meshInfo.matrix);
      }

      mesh.is2d = true; // still required for VertexBuffer.#prepareDraw
      mesh.dbId = this.fragments.fragId2dbId[fragId] | 0;
    }

    // If we would leave that true, THREE.js would call UpdateMatrix() for this mesh and
    // overwrite the matrix with another one computed by position, scale, and quaternion.
    mesh.matrixAutoUpdate = false;

    // Add the mesh to the render group for this fragment
    // Note each render group renders potentially many fragments.
    mesh.frustumCulled = false; //we do our own culling in RenderQueue, the renderer doesn't need to

    // keep fragId and dbId
    mesh.fragId = fragId;
    mesh.modelId = this.modelId;

    // If a model matrix already exists, update matrixWorld (like it's done in setModelMatrix)
    // It's possible that the matrix was set before all vizmeshes were set.
    if (this.matrix) {
      mesh.matrixWorld.multiplyMatrices(this.matrix, mesh.matrix);
    }

    // cache the mesh in this.vizmeshes
    this.vizmeshes[fragId] = mesh;

    this.setFlagFragment(fragId, MeshFlags.MESH_VISIBLE, true);

    // If there are updates pending because we made a change before the fragment
    // existed for a given dbId, update it now.
    this.#updateVertexBufferPendingThemeChanges(fragId);

    for (const callback of this._meshSetCallbacks) {
      callback(fragId);
    }
  }

  // Swaps r/b channels in a THREE.Color object.
  static #swapRBChannels(color) {
    const tmpR = color.r;
    color.r = color.b;
    color.b = tmpR;
    return color;
  }

  /** Linear interpolation between original color and theming color based on theming intensity.
   * @param origColor    {number}        original uint32 color from vertex-buffer. alpha is vertex-opacity
   * @param themingColor {THREE.Vector4} theming color as vec4f. Channels are (r,g,b,a) where alpha is theming intensity.
   * @returns finalColor {number}        final color as uint32
   */
  #applyThemingColorAndVisibility(origColor, themingColor) {
    const rgbMask = 16777215; // = parseInt("00FFFFFF", 16);
    const alphaMask = 4278190080; // = parseInt("FF000000", 16);

    tmp1.set(origColor & rgbMask);

    // THREE.Color denotes uint color in BGRA order (i.e., Blue in the lowest byte).
    // In the vertex-buffer, we use RGBA - so we have to swap when converting between these two.
    InstanceCollection2D.#swapRBChannels(tmp1);

    if (themingColor) {
      // set tmp2 to theming color
      tmp2.setRGB(themingColor.x, themingColor.y, themingColor.z);

      // blend original color with theming color
      tmp1.lerp(tmp2, themingColor.w);
    }

    // convert back to color-buffer uint32 and preserve original alpha bits
    return InstanceCollection2D.#swapRBChannels(tmp1).getHex() | origColor & alphaMask;
  }

  // Updates the per-vertex array of a mesh to reflect latest theming and ghosting state.
  // Note that this can only work on F2D meshes with known attributes and interleaved vertex buffer.
  #updateVertexBufferForThemingAndGhosting(fragId) {
    // get backup of original per-vertex colors (undef if color array is currently not modified)
    var origColors = this.originalColors[fragId];

    // get values to access colors and ids
    var geom = this.getGeometry(fragId);
    if (!geom.vb) {
      console.error("Unexpected non-interleaved vertex buffer");
      return;
    }
    const vbr = new VertexBufferReader(geom);
    const vbw = new VertexBufferWriter(geom);

    if (!vbr.isInterleavedVb) {
      // we cannot work on this mesh.
      return;
    }

    // Track if any colors/layers are affected by theming/ghosting. If not, we can drop the color/layer array backup at the end.
    var themingApplied = false;

    // Constants used for ghosting of 2D objects
    var PaperLayer = 0; // we use the paper layer to determine the paper sheet background (see F2d.js initSheet). This shape must be excluded from ghosting.
    var vertexCount = vbr.vcount;
    // update vertex-color for each vertex
    for (var i = 0; i < vertexCount; i++) {
      var dbId = vbr.getDbIdAt(i);
      var color = origColors ? origColors[i] : vbr.getColorAt(i);
      var layer = vbr.getLayerIndexAt(i);

      // sign extend the upper byte to get back negative numbers (since per-vertex ids are clamped from 32 bit to 24 bit)
      dbId = dbId << 8 >> 8;

      var isPaper = dbId === -1 && layer === PaperLayer;

      // is this id affected by theming?
      var themeColor = this.db2ThemingColor[dbId];
      var isHidden = this.dbIdOpacity[dbId] === 0;

      if (!themeColor && !isHidden) {
        // no theming for this vertex
        if (origColors) {
          // restore original color
          color = origColors[i];
        } // else: if there is no backup array, the vertex-color is already the original.
      } else {
        // this vertex-color will be affected by theming.
        // make sure that we have backup.
        if (!origColors) {
          // backup original colors before we modify them.
          origColors = new Uint32Array(vertexCount);
          for (var j = 0; j < vertexCount; j++) {
            origColors[j] = vbr.getColorAt(j);
          }
          this.originalColors[fragId] = origColors;
        }

        // replace vertex-color based on theming and visibility
        if (isHidden) {
          color = 0;
        } else {
          color = this.#applyThemingColorAndVisibility(color, themeColor);
        }

        // signal that the color backup array is still needed
        themingApplied = true;
      }

      if (!isHidden) {
        let opacity = this.dbIdOpacity[dbId];
        if (!isNaN(opacity)) {
          const rgbMask = parseInt("00FFFFFF", 16);
          opacity = 255 * opacity << 24;
          color = color & rgbMask | opacity;
        }
      }

      // color -> vertexBuffer
      vbw.setColorAt(i, color);

      // is this id affected by theming?
      var isGhosted = this.dbIdIsGhosted[dbId] && !isPaper;
      var flags = vbr.getVertexFlagsAt(i);
      if (isGhosted)
      flags |= 0xff << 24;else

      flags &= ~(0xff << 24);

      // layer -> vertexBuffer
      vbw.setVertexFlagsAt(i, flags);
    }

    // if theming is off for all vertices, drop the backup array
    if (!themingApplied) {
      this.originalColors[fragId] = null;
    }

    // trigger refresh of GPU-side vertex buffer
    geom.vbNeedsUpdate = true;
  }

  #updateVertexBufferForThemingAndGhostingIfNeeded(fragId) {
    // Check if anything changed
    if (!this.themingOrGhostingNeedsUpdate[fragId]) {
      return;
    }

    this.#updateVertexBufferForThemingAndGhosting(fragId);

    // Don't touch this mesh again until new theming changes are done
    this.themingOrGhostingNeedsUpdate[fragId] = false;
  }

  #updateVertexBufferPendingThemeChanges(fragId) {
    if (!isObjectEmpty(this.themingOrGhostingNeedsUpdateByDbId)) {
      let dbIds = this.getDbIds(fragId);
      dbIds = Array.isArray(dbIds) ? dbIds : [dbIds];
      dbIds = dbIds.filter((dbId) => this.themingOrGhostingNeedsUpdateByDbId[dbId]);
      if (dbIds.length) {
        this.#updateVertexBufferForThemingAndGhosting(fragId);

        // In the 2D case the mapping is 1 -> many (1 frag -> many dbIds), so after updating this frag we can remove the dbIds from the array
        dbIds.forEach((dbId) => {
          delete this.themingOrGhostingNeedsUpdateByDbId[dbId];
        });
      }
    }
  }

  /**
   * Provides an actual mesh for specific fragment.
   * @param {number} fragId - Fragment ID.
   * @returns {THREE.Mesh} Mesh for the given fragment.
   */
  getVizmesh(fragId) {
    // make sure that vertex-colors reflect the latest theming-state
    this.#updateVertexBufferForThemingAndGhostingIfNeeded(fragId);
    const mesh = this.vizmeshes[fragId];
    if (mesh) {
      mesh.themingColor = this.db2ThemingColor[mesh.dbId];
    }
    return mesh;
  }

  getMaterial(fragId) {
    return this.vizmeshes[fragId].material;
  }

  hasMaterial(fragId) {
    return this.vizmeshes[fragId].material;
  }

  getGeometry(fragId) {
    // Make sure this.vizmesh[fragId] isn't null or undefined
    return this.vizmeshes[fragId]?.geometry || null;
  }

  hasGeometry(fragId) {
    return !!this.vizmeshes[fragId]?.geometry;
  }

  /**
   * TODO: we should be consistent throughout the full lmv core with naming these IDs.
   * Sometimes is idx, index, or id, and also naming of svf/db/frag/ etc can become confusing.
   * Lets strictly differentiate between PropertyID, FragmentID, MeshID, and GeometryID.
   * And lets also strictly differentiate between index:idx and identifier:id ...
   */
  getGeometryId(fragId) {
    // When using THREE.Meshes, fragIds and geomids are the same and this.geomids is not used.
    return fragId;
  }

  setMaterial(fragId, material, fromLoader = false) {
    this.vizmeshes[fragId].material = material;

    for (const callback of this.materialChangedCallbacks) {
      callback(fragId, material, fromLoader);
    }
  }

  getCount() {
    return this.vizmeshes.length;
  }

  dispose() {
    // dispatch remove event to all meshes and dispose events to all BufferGeometry buffers
    // This will trigger EventListeners added by WebGLRenderer that deallocate the geometry later.
    // (see onGeometryDispose(..) in WebGLRenderer.js)
    var DISPOSE_EVENT = { type: 'dispose' };
    var REMOVED_EVENT = { type: 'removed' };
    for (var i = 0; i < this.vizmeshes.length; i++) {
      var m = this.vizmeshes[i];
      if (m) {
        m.dispatchEvent(REMOVED_EVENT);
        m.geometry.dispatchEvent(DISPOSE_EVENT);
      }
    }
  }

  dtor() {
    this.dispose();
    super.dtor();

    this.vizmeshes = null;

    this.originalColors = null;
    this.themingOrGhostingNeedsUpdate = null;
    this.themingOrGhostingNeedsUpdateByDbId = null;
    this.dbIdOpacity = null;
    this.dbIdIsGhosted = null;
  }

  setAllVisibility(value) {
    var frags = this.fragments;
    if (frags && frags.dbId2fragId) {
      for (var id in frags.dbId2fragId) {
        this.setObject2DGhosted(parseInt(id), !value);
      }
    }

    super.setAllVisibility(value);
  }

  /**
   * Returns world matrix of a fragment.
   * @param {number} index - Fragment ID.
   * @param {THREE.Matrix4} dstMtx - Out param to receive the matrix.
   */
  getOriginalWorldMatrix(index, dstMtx) {
    // get matrix directly from THREE.Mesh
    var m = this.vizmeshes[index];
    if (m) {
      dstMtx.copy(m.matrix); // Was matrixWorld, but that is now being used for the alignment transform
    } else {
      dstMtx.identity();
    }
  }

  /**
   * Writes the final world matrix of a fragment to out param dstMtx.
   * The world matrix results from original transform and anim transform (if any).
   * @param {number} index - Fragment ID.
   * @param {THREE.Matrix4} dstMtx - Out param to receive the matrix.
   */
  getWorldMatrix(index, dstMtx) {
    this.getOriginalWorldMatrix(index, dstMtx);

    // Apply optional model-transform
    if (this.matrix) {
      // Apply fragment matrix first (=dst), then model matrix (=this.matrix).
      // Note that model matrix must be multiplied from left for this.
      dstMtx.multiplyMatrices(this.matrix, dstMtx);
    }
  }

  setModelMatrix(matrix) {
    if (matrix) {
      this.matrix = this.matrix || new Matrix4(true);
      this.matrix.copy(matrix);
      // For ThreeJS meshes we use matrixWorld to store the alignment matrix
      this.vizmeshes.forEach((mesh) => {
        mesh.matrixWorld.multiplyMatrices(this.matrix, mesh.matrix);
      });
    } else {
      this.matrix = null;
      this.vizmeshes.forEach((mesh) => {
        mesh.matrixWorld.copy(mesh.matrix);
      });
    }
    this.invMatrix = null;

    // Only needed for WebGPU (otherwise, there are no callbacks).
    // Note that although this call changes the result of getWorldMatrix() for all fragments, the
    // transformChangedCallbacks are not fired to avoid invoking per-fragment callbacks for the entire model.
    // Instead the _modelMatrixChangedCallbacks inform listeners the entire model has changed.
    for (const callback of this._modelMatrixChangedCallbacks) {
      callback();
    }
  }

  /**
   * Writes the world box to dstBox outparams, considering matrix and anim transform (if specified).
   * @param {number} index - Fragment ID.
   * @param {THREE.Box3|LmvBox3} dstBox - result is saved here
   */
  getWorldBounds(index, dstBox) {
    // get original model box
    // either from THREE.Mesh
    var m = this.vizmeshes[index];
    if (m && m.geometry) {
      dstBox.copy(m.geometry.boundingBox);
    }

    if (this.viewBounds) {
      // Crop the box by the viewing bounds
      dstBox.intersect(this.viewBounds);
    }

    if (!dstBox.isEmpty()) {
      // apply world matrix to dstBox.
      // Note that the worldMatrix includes the model matrix as well.
      this.getWorldMatrix(index, _tmpMatrix);
      dstBox.applyMatrix4(_tmpMatrix);
    } else {
      // dstBox could be empty from intersect, but not on all axis.
      // Make sure all coordinates are really empty so we don't add anything irrelevant
      // when accumulating the result.
      dstBox.makeEmpty();
    }
  }

  /**
   * Writes the original (as loaded) world box to dstBox outparams. Does not take into account changes
   * to object position like explode/animation or model matrix.
   * @param {number} index - Fragment ID.
   * @param {Array} dstBox - Array where result is stored as 6 consecutive numbers
   */
  getOriginalWorldBounds(index, dstBox) {
    // get original model box
    // either from THREE.Mesh
    var m = this.vizmeshes[index];
    if (m && m.geometry) {
      _tmpBox.copy(m.geometry.boundingBox);
    }

    if (!_tmpBox.isEmpty()) {
      // apply world matrix to dstBox
      this.getOriginalWorldMatrix(index, _tmpMatrix);
      _tmpBox.applyMatrix4(_tmpMatrix);
    }

    dstBox[0] = _tmpBox.min.x;
    dstBox[1] = _tmpBox.min.y;
    dstBox[2] = _tmpBox.min.z;
    dstBox[3] = _tmpBox.max.x;
    dstBox[4] = _tmpBox.max.y;
    dstBox[5] = _tmpBox.max.z;
  }

  /**
   * Set themingNeedsUpdate flag for all vizmeshes that contain a given dbId
   * @param {number} dbId
   */
  #setThemingOrGhostingNeedsUpdateFlag(dbId) {
    // get id(s) of affected mesh(es) that needs a vertex-color update
    var fragIds = this.fragments.dbId2fragId[dbId];

    //  trigger update for single id or an array of ids
    if (Array.isArray(fragIds)) {
      for (var i = 0; i < fragIds.length; i++) {
        this.themingOrGhostingNeedsUpdate[fragIds[i]] = true;
      }
    } else if (typeof fragIds === 'number') {
      this.themingOrGhostingNeedsUpdate[fragIds] = true;
    } else {
      // In case the fragment doesn't exist, it might be that is hasn't been loaded yet.
      // Keep the needsUpdate by dbId in case it's loaded later.
      this.themingOrGhostingNeedsUpdateByDbId[dbId] = true;
    }
    this.themingOrGhostingIsDirty = true;
  }

  /**
   * Applies a theming color that is blended with the final fragment color of a material shader.
   * @param {number}        dbId
   * @param {THREE.Vector4} [color] - theming color (in xyz) and intensity (in w). All components in [0,1].
   *                                  Set to undefined for 'no theming'
   */
  setThemingColor(dbId, color) {
    // Stop if color keeps the same
    var oldColor = this.db2ThemingColor[dbId];
    var colorsEqual = oldColor === color || oldColor && color && oldColor.equals(color);
    if (!colorsEqual) {
      this.#setThemingOrGhostingNeedsUpdateFlag(dbId);
      super.setThemingColor(dbId, color);
    }
  }

  /** Restore original colors for all themed shapes. */
  clearThemingColors() {
    // When using F2D (model.is2d()==true), we have to update the restore the original
    // per-vertex colors. For 3D, we can use per-shape colors, so that this step is not
    // needed.
    // trigger update for all meshes that were affected by theming before
    // Note that dbId2fragId only exists for F2D models.
    for (var id in this.fragments.dbId2fragId) {
      this.#setThemingOrGhostingNeedsUpdateFlag(parseInt(id));
    }
    delete this.db2ThemingColor['-1']; // Delete theming for sheet

    super.clearThemingColors();
  }

  /** Set ghosting flag for a 2D object. This reduces the objects opacity, blends it with pageColor, and excludes it from selection.
   *  @param {number} dbId
   *  @param {bool}   state
   */
  setObject2DGhosted(dbId, state) {
    var oldState = this.dbIdIsGhosted[dbId];
    if (!!state !== !!oldState) {
      this.dbIdIsGhosted[dbId] = state;
      this.#setThemingOrGhostingNeedsUpdateFlag(dbId);
    }
  }

  /** Set an opacity value for a 2D object.
   *  @param {number} dbId
   *  @param {number}  opacity
   */
  setObject2DOpacity(dbId, opacity) {
    var oldOpacity = this.dbIdOpacity[dbId];
    if (opacity !== oldOpacity) {
      this.dbIdOpacity[dbId] = opacity;
      this.#setThemingOrGhostingNeedsUpdateFlag(dbId);
    }
  }

  /** Set hide flag for a 2D object. This sets opacity to 0.0, which also excludes it from selection.
   *  @param {number} dbId
   *  @param {bool}   visible
   */
  setObject2DVisible(dbId, visible) {
    var wasVisible = this.dbIdOpacity[dbId] !== 0;
    if (visible !== wasVisible) {
      this.dbIdOpacity[dbId] = visible | 0;
      this.#setThemingOrGhostingNeedsUpdateFlag(dbId);
    }
  }

  getDoNotCut() {
    return this.doNotCut;
  }

  setDoNotCut(doNotCut) {
    this.doNotCut = doNotCut;
  }

  resize(size) {
    if (this.vizflags.length <= size) {
      // Gradually should only used if isFixedSize is false (as used for F2D geometry)
      // determine new length of all per-fragmentId arrays
      let nlen = Math.ceil(1.5 * Math.max(this.vizflags.length, size)) || 1;
      if (nlen < this.vizmeshes.length) {
        nlen = this.vizmeshes.length;
      }

      super.resize(nlen);
    }
  }
}